import { ComponentType, createContext, PropsWithChildren } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useSimpleReducer } from "@common/hooks";
import { DeviceTab } from "@pages/Assets/Device/const";

export type TabType = {
  name: string;
  disabled?: boolean;
  required?: boolean;
  tooltip?: string;
  hidden?: boolean;
};

export type ViewTabType = {
  tabs: TabType[];
  note?: string;
  selectedTab: string;
  isTabsUpdatedWithPermissions?: boolean;
};

export type ViewTabContext = ViewTabType & {
  dispatch: (changes: Partial<ViewTabType>) => void;
  setSelectedTab: (newTab: string) => void;
};

const defaultContext: ViewTabContext = {
  tabs: [{ name: DeviceTab.Realtime }],
  selectedTab: DeviceTab.Realtime,
  isTabsUpdatedWithPermissions: false,
  dispatch: () => {},
  setSelectedTab: () => {},
};

export const ViewTabContext = createContext<ViewTabContext>(defaultContext);

export const ViewTabProvider = (
  props: PropsWithChildren<Partial<ViewTabContext>>
) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, dispatch] = useSimpleReducer<ViewTabContext>({
    ...defaultContext,
    ...props,
  });

  const selectedTab = searchParams.get("tab") || state.tabs[0].name;

  const setSelectedTab = (selectedTab: string) => {
    setSearchParams(
      (params) => {
        const currentTab = params.get("tab");
        const isTabUpdated =
          state.isTabsUpdatedWithPermissions && selectedTab !== currentTab;
        const paramsObj = isTabUpdated ? {} : params;
        const updatedParams = new URLSearchParams(paramsObj);

        updatedParams.set("tab", selectedTab);

        return updatedParams;
      },
      { state: location.state, replace: true }
    );
  };

  return (
    <ViewTabContext.Provider
      value={{ ...state, selectedTab, setSelectedTab, dispatch }}
    >
      {props.children}
    </ViewTabContext.Provider>
  );
};

export const WithTabsProvider =
  <T extends object>(Component: ComponentType<T>) =>
  (props: T & { tabsProviderProps?: Partial<ViewTabContext> }) => {
    const { tabsProviderProps, ...componentProps } = props;

    return (
      <ViewTabProvider {...tabsProviderProps}>
        <Component {...(componentProps as T)} />
      </ViewTabProvider>
    );
  };
